/**
 * The service worker's side of the update prompt (task 14.2).
 *
 * The requirement is that a new build **never takes over on its own**. The
 * worker is registered with `registerType: 'prompt'` and `skipWaiting: false`,
 * so a new one installs and then waits; this module notices, and the player
 * decides.
 *
 * Two things are deliberately separated here:
 *
 *   - the **bridge**, a two-method interface that says a build is waiting and
 *     applies it. That is all the application knows about service workers;
 *   - the **registration**, which imports `virtual:pwa-register` — a module that
 *     only exists inside a Vite build. It is called from `main.tsx` and from
 *     nowhere else, so a unit test can exercise every path through the prompt
 *     without a bundler in the room.
 *
 * Applying an update reloads the page. Whatever is on screen has to be on disk
 * first, which is why `applyUpdate` in the view store flushes the save before
 * it calls through here.
 */

/** What the application knows about service worker updates. */
export interface UpdateBridge {
  /**
   * Applies the waiting build and reloads. Resolves only if the reload does
   * not happen, which is why callers must not depend on it resolving.
   */
  apply(): Promise<void>;
}

export interface RegisterOptions {
  /** Called when a new build has installed and is waiting to be applied. */
  onWaiting(bridge: UpdateBridge): void;
  /** Called once the worker has taken responsibility for offline launches. */
  onReady?: (() => void) | undefined;
}

/**
 * Registers the service worker, if this browser has one and this build shipped
 * one.
 *
 * Resolves to `false` where there is nothing to register — an unsupported
 * browser, or a development server with the worker disabled — which is a
 * perfectly good state: the game plays online and simply is not installable.
 */
export async function registerServiceWorker(options: RegisterOptions): Promise<boolean> {
  if (!('serviceWorker' in globalThis.navigator)) return false;

  try {
    // A literal specifier, so Vite resolves the virtual module at build time —
    // a computed one is left alone and fails at runtime, which registers no
    // worker at all and quietly costs the game its offline launch.
    //
    // Dynamic so the module is only reached when this function is called, and
    // this function is called from `main.tsx` and nowhere else. Every test
    // imports `UpdateBridge` as a *type*, which is erased, so nothing outside a
    // Vite build ever has to resolve it.
    const { registerSW } = await import('virtual:pwa-register');

    const updateServiceWorker = registerSW({
      immediate: true,
      onNeedRefresh: () => {
        options.onWaiting({ apply: () => updateServiceWorker(true) });
      },
      onOfflineReady: () => {
        options.onReady?.();
      },
    });
    return true;
  } catch {
    // A failed registration is not a failed launch. The application is a
    // perfectly ordinary web page without a service worker; it just will not
    // start on a plane.
    return false;
  }
}
